import { Ignition } from'../../../pip/library/pi-interop/pi-interop.js';
import memory from'../../../pip/library/pi-memory/pi-memory.js';
import f1 from'../../../pip/library/pi-f1/pi-f1.js';

import { IRequestInterceptorIO, IResponseInterceptorIO, IInterceptorIO, TInfoIO, TOptions } from './interceptor';
import { t_adapter, t_interceptor } from './io';
import { t_io_data } from 'src/helpers/raw_data';

export interface IAdapterIO<REQ, RES, OPTS extends TOptions> {
  readonly type: t_adapter;

  options(): OPTS;
  options(ops: OPTS): this;
  options(ops?: OPTS): this | OPTS;

  intercept(iio: IRequestInterceptorIO<REQ> | IResponseInterceptorIO<RES>): Promise<this>;
  unintercept(iio: IRequestInterceptorIO<REQ> | IResponseInterceptorIO<RES>): Promise<this>;

  interceptors(interceptor: 'request'): IRequestInterceptorIO<REQ>[];
  interceptors(interceptor: 'response'): IResponseInterceptorIO<RES>[];
  interceptors(interceptor: t_interceptor): IRequestInterceptorIO<REQ>[] | IResponseInterceptorIO<RES>[];

  query(path: REQ): Promise<RES>;
  query(path: string, request?: REQ): Promise<RES>;
  query(pathOr: REQ | string, request?: REQ): Promise<RES>;

  /**
   * create a controller for a push method
   *
   * @returns {IControllerIO}
   * @memberof AdapterIO
   */
  controller(): IControllerIO;

  /**
   * Add a named adapter to this adapter so all interceptors are executed.
   * Interceptors from included adapters are always executed first.
   */
  include(id: string): this;
}

/**
 *
 *
 * @interface IRequestIO
 */
export interface IRequestIO {
  _req?: never;
  /**
   * used if push receives just a request
   */
  path?: string;
  /**
   * An AbortSignal to set request's signal.
   */
  signal?: AbortSignal | null;
  /**
   * a controller can be created to manipulate the internal state of the push operation
   */
  controller?: IControllerIO;
  /**
   * A value to push/pull to an io service
   */
  data?: t_io_data;
}

export interface IResponseIO {
  _req?: never;
  /**
   * A value to receive from an io service on push/pull
   */
  data: t_io_data | null;
}

export interface IControllerIO {
  ac: AbortController;
  abort: () => void;
}

/**
 *
 *
 * @export
 * @class AdapterIO
 * @implements {IAdapterIO<REQ, RES>}
 * @template REQ
 * @template RES
 */
export class AdapterIO<REQ extends IRequestIO, RES extends IResponseIO, OPTS extends TOptions>
  implements IAdapterIO<REQ, RES, OPTS> {
  protected _responses: IResponseInterceptorIO<RES>[] = [];
  protected _requests: IRequestInterceptorIO<REQ>[] = [];
  protected _ignition = new Ignition();
  protected _profiles: string[] = [];
  protected _options: OPTS;

  static noopAdapter: AdapterIO<IRequestIO, IResponseIO, TOptions>;

  public readonly type: t_adapter;

  constructor(public id?: string) {
    if (this.id) {
      memory.shared<AdapterIO<REQ, RES, OPTS>>(AdapterIO.noopAdapter, this.__profileId(this.id), this);
    }
  }

  options(): OPTS;
  options(ops: OPTS): this;
  options(opts?: OPTS): OPTS | this {
    if (opts) {
      this._options = opts;
      return this;
    }

    return this._options;
  }

  async intercept(iio: IRequestInterceptorIO<REQ> | IResponseInterceptorIO<RES>): Promise<this> {
    const type: t_interceptor = iio.type;
    const list = this.__interceptorList(type);
    const _iio = await this._ignition.startup(iio, {});
    const _interceptors = this[list];

    this[list] = f1.push(_iio, _interceptors);
    return this;
  }

  /**
   *
   *
   * @param {t_interceptor} interceptor
   * @param {IInterceptorIO<REQ, RES>} fn
   * @returns {this}
   * @memberof AdapterIO
   */
  async unintercept(iio: IRequestInterceptorIO<REQ> | IResponseInterceptorIO<RES>): Promise<this> {
    const type: t_interceptor = iio.type;
    const list = this.__interceptorList(type);
    const _iio = await this._ignition.shutdown(iio);
    const _interceptors = this[list];

    this[list] = f1.remove((__iio: IInterceptorIO<unknown>) => __iio.id === _iio.id, _interceptors);
    return this;
  }

  /**
   * read the interceptors for an IO
   *
   * @param {t_interceptor} interceptor
   * @returns {IInterceptorIO<REQ, RES>[]}
   * @memberof AdapterIO
   */
  interceptors(interceptor: 'request'): IRequestInterceptorIO<REQ>[];
  interceptors(interceptor: 'response'): IResponseInterceptorIO<RES>[];
  interceptors(interceptor: t_interceptor): IRequestInterceptorIO<REQ>[] | IResponseInterceptorIO<RES>[] {
    return f1.concat([], this[this.__interceptorList(interceptor)]);
  }

  private __interceptorList(interceptor: t_interceptor) {
    return `_${interceptor}s`;
  }

  async query(path: REQ): Promise<RES>;
  async query(path: string, request?: REQ): Promise<RES>;
  async query(pathOr: REQ | string, request?: REQ): Promise<RES> {
    const options = { ...this._options };
    const path = typeof pathOr === 'string' ? pathOr : `${pathOr.path}`;
    const _request: REQ = typeof pathOr === 'string' ? f1.safe<REQ, REQ>({} as REQ, request) : (pathOr as REQ);

    if (!_request.signal && _request.controller) {
      _request.signal = _request.controller.ac.signal;
    }

    // @todo next three lines could be called once if a `optimized` method is added
    const profiles = this._profiles
      .map((pid) => memory.shared<AdapterIO<REQ, RES, OPTS>>(undefined, this.__profileId(pid)))
      .filter(f1.identity);

    const responses = f1.concat(
      profiles.flatMap((p) => p._responses),
      this._responses
    );

    const requests = f1.concat(
      profiles.flatMap((p) => p._requests),
      this._requests
    );

    const req = await this._run(requests, { path, packet: _request, options });
    const payload = await this._query(req.path, req.packet);
    const out = await this._run(responses, { path, packet: payload, options });

    return out.packet;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async _query(path: string, request: REQ): Promise<RES> {
    return Promise.resolve({} as RES);
  }

  /**
   * create a controller to handle abortion
   */
  controller(): IControllerIO {
    const ac = new AbortController();
    return {
      ac,
      abort: () => ac.abort()
    };
  }

  /**
   * include named io(s) in this io. All interceptors will be executed.
   *
   * The push/pull function will collect all transformations from all included io(s),
   * so if you add more interceptors to a named io after the include they will all
   * be applied when push/pull are invoked.
   */
  include(id: string): this {
    this._profiles = f1.unique(f1.push(id, this._profiles));
    return this;
  }

  /**
   * unique memory namespace for saved io
   */
  __profileId(id: string) {
    return `io-profiles-${id}`;
  }

  /**
   * apply all transformations on an `infoIO`
   *
   * @protected
   * @template V
   * @param {((infoIO: TInfoIO<V>) => Promise<TInfoIO<V>>)[]} interceptors
   * @param {TInfoIO<V>} infoIO
   * @memberof AdapterIO
   * @todo replace with f1.funnel
   */
  protected async _run<V>(interceptors: IInterceptorIO<V>[], infoIO: TInfoIO<V>) {
    for await (const iio of interceptors) {
      infoIO = await iio.transform(infoIO);
    }

    return infoIO;
  }
}
